import { DgraphTokenManager } from './dgraphTokenManager';

/**
 * Handles browser visibility changes and manages connection refresh throttling
 * to prevent mass reconnections when tabs become active after being idle.
 */
export class BrowserVisibilityHandler {
  private static instance: BrowserVisibilityHandler | null = null;
  private tokenManager: DgraphTokenManager | null = null;
  private lastRefreshTime: number = 0;
  private lastVisibilityEventTime: number = 0;
  private readonly DEBOUNCE_INTERVAL = 30000;
  private readonly EVENT_DEDUPE_MS = 1000;
  private isListening = false;
  private boundVisibilityHandler: (() => void) | null = null;
  private boundFocusHandler: (() => void) | null = null;

  private constructor() {}

  static getInstance(): BrowserVisibilityHandler {
    if (!BrowserVisibilityHandler.instance) {
      BrowserVisibilityHandler.instance = new BrowserVisibilityHandler();
    }
    return BrowserVisibilityHandler.instance;
  }

  initialize(tokenManager: DgraphTokenManager): void {
    this.tokenManager = tokenManager;
    this.startListening();
  }

  private startListening(): void {
    if (this.isListening || typeof document === 'undefined') {
      return;
    }

    this.boundVisibilityHandler = this.handleVisibilityChange.bind(this);
    this.boundFocusHandler = this.handleWindowFocus.bind(this);

    document.addEventListener('visibilitychange', this.boundVisibilityHandler);
    window.addEventListener('focus', this.boundFocusHandler);

    this.isListening = true;
  }

  private handleVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      this.handleTabBecameVisible();
    }
  }

  private handleWindowFocus(): void {
    this.handleTabBecameVisible();
  }

  private handleTabBecameVisible(): void {
    if (!this.tokenManager) {
      return;
    }

    const now = Date.now();

    if (now - this.lastVisibilityEventTime < this.EVENT_DEDUPE_MS) {
      return;
    }
    this.lastVisibilityEventTime = now;

    const timeSinceLastRefresh = now - this.lastRefreshTime;

    if (timeSinceLastRefresh > this.DEBOUNCE_INTERVAL) {
      this.lastRefreshTime = now;

      setTimeout(() => {
        if (this.tokenManager) {
          this.tokenManager.manualRefresh().catch(error => {
            console.warn('Visibility-triggered refresh failed:', error);
          });
        }
      }, 500);
    }
  }

  stopListening(): void {
    if (!this.isListening || typeof document === 'undefined') {
      return;
    }

    if (this.boundVisibilityHandler) {
      document.removeEventListener('visibilitychange', this.boundVisibilityHandler);
    }
    if (this.boundFocusHandler) {
      window.removeEventListener('focus', this.boundFocusHandler);
    }

    this.isListening = false;
    this.boundVisibilityHandler = null;
    this.boundFocusHandler = null;
  }

  cleanup(): void {
    this.stopListening();
    this.tokenManager = null;
    this.lastRefreshTime = 0;
    this.lastVisibilityEventTime = 0;
  }

  getStatus() {
    return {
      isListening: this.isListening,
      hasTokenManager: !!this.tokenManager,
      lastRefreshTime: this.lastRefreshTime,
      timeSinceLastRefresh: Date.now() - this.lastRefreshTime,
      debounceInterval: this.DEBOUNCE_INTERVAL,
    };
  }
}

export function initializeBrowserVisibilityHandler(
  tokenManager: DgraphTokenManager,
): BrowserVisibilityHandler {
  const handler = BrowserVisibilityHandler.getInstance();
  handler.initialize(tokenManager);
  return handler;
}
